import { Avatar } from '../brief/primitives';
import { siteLabel } from '../../lib/sites';
import { relativeTime } from '../../lib/formatting';
import { PageMast, MastTile } from '../shared/workroom';
import type { DetailBack } from '../../hooks/useDetailBack';
import type { TeamStandupData, StandupMember, StandupItem, StandupCategory } from '../../hooks/useTeamStandupSurface';

/*
 * TeamStandupSurface — the "Full standup" page, a child of the daily brief. A standard,
 * retrospective standup: "yesterday, what was accomplished", one section per teammate,
 * most-active/most-relevant first (ordering owned by the BFF). Presentational — the route wrapper
 * owns the useTeamStandupSurface hook and passes `data`, `onNav` and the back contract.
 *
 * Ported to the redesign register (PageMast + card grid under a `.rd` root) so it reads as the same
 * product as Briefing / Actions / Live. It was the last surface still on the pre-port hand-written
 * `.stand-*` CSS, which is how it ended up looking like a different app — and, because nothing
 * linked to it, nobody noticed.
 *
 * Legibility choices, deliberately: the narrative is the primary read at a comfortable measure
 * (~62ch) and 1.65 line-height; the category is a WORD, not a coloured dot plus the same word again;
 * colour is carried only by that one word, so a person's card is otherwise monochrome.
 */

// One restrained tint per category. Kept to text colour — the card itself stays monochrome so a
// long standup does not read as a scatter of dots.
function catClass(cat: StandupCategory): string {
  return cat === 'shipped'
    ? 'text-[#4d7c52] dark:text-[#8fbf95]'
    : cat === 'still_building'
      ? 'text-acc'
      : 'text-[#9a7b3f] dark:text-[#d3b06a]';
}

function catLabel(cat: StandupCategory): string {
  return cat === 'shipped' ? 'Shipped' : cat === 'still_building' ? 'Still building' : 'Discussed';
}

function standupDate(iso: string | null): string {
  if (!iso) return 'Standup';
  const day = new Date(iso)
    .toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    .replace(',', '');
  return `Standup · ${day}`;
}

// Fallback row — used only when the backend narrative is absent (LLM degraded), so nothing is lost.
function StandupItemRow({ item, onNav }: { item: StandupItem; onNav: (id: string) => void }) {
  const clickable = Boolean(item.conversationId);
  return (
    <div
      data-standup-item
      className={`border-t border-border py-3 first:border-t-0 first:pt-0${clickable ? ' cursor-pointer transition hover:opacity-80' : ''}`}
      onClick={clickable ? () => onNav(`conversations/${item.conversationId}`) : undefined}
    >
      <p className="text-[15px] leading-6">
        <span className={`font-medium ${catClass(item.category)}`}>{catLabel(item.category)}</span>
        <span className="text-muted-foreground"> · </span>
        <span className="font-medium">{item.headline}</span>
        {item.why ? <span className="text-muted-foreground"> — {item.why}</span> : null}
      </p>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        {item.project && <span>{item.project}</span>}
        {item.site && <span>{siteLabel(item.site)}</span>}
        <span>{relativeTime(item.timestamp)}</span>
      </p>
    </div>
  );
}

function StandupPerson({ member, onNav }: { member: StandupMember; onNav: (id: string) => void }) {
  // Text-first: the plain-words narrative is the primary read, with the underlying items reduced to
  // compact clickable source chips.
  const sources = member.items.filter((it) => it.conversationId);

  return (
    // data-* hooks are the test surface: the render tests used to assert on CSS class names, which
    // made a restyle look like a behaviour regression.
    <article data-standup-person className="rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)]">
      <header className="flex items-center gap-3">
        <Avatar name={member.displayName} size={24} />
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <h2 className="truncate text-[15px] font-semibold leading-5">{member.displayName}</h2>
            {member.isYou && (
              <span data-standup-you className="rounded-full border border-border px-1.5 py-px text-[10px] leading-4 text-muted-foreground">
                You
              </span>
            )}
          </div>
          <p className="truncate text-xs text-muted-foreground">
            {member.displayRole}
            {member.lastActive ? ` · ${relativeTime(member.lastActive)}` : ''}
          </p>
        </div>
        <span className="ml-auto shrink-0 font-['DM_Mono'] text-[10px] text-muted-foreground">
          {member.itemCount} update{member.itemCount === 1 ? '' : 's'}
        </span>
      </header>

      {member.narrative ? (
        <>
          <p data-standup-narrative className="mt-4 max-w-[62ch] text-[15px] leading-[1.65]">{member.narrative}</p>
          {sources.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {sources.map((it, i) => (
                <button
                  key={it.conversationId || `src-${i}`}
                  type="button"
                  data-standup-source
                  className="inline-flex max-w-full items-baseline gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-left text-xs transition hover:border-acc hover:text-acc"
                  onClick={() => onNav(`conversations/${it.conversationId}`)}
                  title={it.headline}
                >
                  <span className={`shrink-0 text-[10px] ${catClass(it.category)}`}>{catLabel(it.category)}</span>
                  <span className="min-w-0 truncate text-muted-foreground">{it.headline}</span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="mt-4">
          {member.items.map((it, i) => (
            <StandupItemRow key={it.conversationId || `it-${i}`} item={it} onNav={onNav} />
          ))}
        </div>
      )}
    </article>
  );
}

export function TeamStandupSurface({
  data,
  onNav,
  back,
}: {
  data: TeamStandupData;
  onNav: (id: string) => void;
  back?: DetailBack;
}) {
  const members = data.members ?? [];
  const active = members.filter((m) => m.itemCount > 0);
  const quiet = members.filter((m) => m.itemCount === 0);
  const updates = active.reduce((sum, m) => sum + m.itemCount, 0);

  return (
    <div className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground selection:bg-[#d9b298] selection:text-[#3e2f2a]">
      <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
        <button
          type="button"
          onClick={back ? back.go : () => onNav('briefing')}
          className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground transition hover:text-foreground"
        >
          ‹ {back ? back.label : 'Briefing'}
        </button>

        <PageMast
          eyebrow={standupDate(data.generated_at)}
          title={
            <>
              Yesterday, <em>in full.</em>
            </>
          }
          intro={`What ${data.team_name ? data.team_name : 'the team'} got done — most relevant to you first. The brief shows the cut that touches your work; this page holds everyone.`}
          tiles={
            <>
              <MastTile
                label="ACTIVE"
                value={active.length}
                note={`of ${members.length} teammate${members.length === 1 ? '' : 's'}`}
              />
              <MastTile
                label="UPDATES"
                value={updates}
                note={quiet.length > 0 ? `${quiet.length} quiet` : 'everyone moved'}
              />
            </>
          }
        />

        {active.length === 0 ? (
          <div className="rounded-[24px] border border-dashed border-border bg-card/60 px-6 py-14 text-center">
            <p className="text-sm text-muted-foreground">No team activity in the last day.</p>
          </div>
        ) : (
          // grid-cols-1: the mobile track must be minmax(0,1fr) — a cols-less grid floors at
          // the widest chip's min-content, a truncate line's FULL text (bug-720 class).
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {active.map((m) => (
              <StandupPerson key={m.userId} member={m} onNav={onNav} />
            ))}
          </div>
        )}

        {quiet.length > 0 && (
          <p className="mt-6 text-sm text-muted-foreground">
            Quiet yesterday: {quiet.map((m) => m.displayName).join(', ')}
          </p>
        )}
      </div>
    </div>
  );
}
